import { useMemo, useCallback } from 'react'
import { Autocomplete } from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import type { TopologyNode } from '@/api/topology'

interface TopologySearchProps {
  nodes: TopologyNode[]
  onSelect: (nodeId: string) => void
}

export function TopologySearch({ nodes, onSelect }: TopologySearchProps) {
  const { options, valueToNodeId } = useMemo(() => {
    const seen = new Map<string, number>()
    const opts: string[] = []
    const map = new Map<string, string>()

    for (const n of nodes) {
      const base = `${n.data.label} (${n.data.type.toUpperCase()})`
      const count = seen.get(base) ?? 0
      seen.set(base, count + 1)
      // Append index for duplicates to guarantee uniqueness
      const label = count === 0 ? base : `${base} #${count + 1}`
      opts.push(label)
      map.set(label, n.id)
    }

    // Fix first occurrence if it had duplicates
    for (const [base, count] of seen) {
      if (count > 1 && map.has(base)) {
        const nodeId = map.get(base)!
        map.delete(base)
        const newLabel = `${base} #1`
        opts[opts.indexOf(base)] = newLabel
        map.set(newLabel, nodeId)
      }
    }

    return { options: opts, valueToNodeId: map }
  }, [nodes])

  const handleChange = useCallback(
    (value: string) => {
      const nodeId = valueToNodeId.get(value)
      if (nodeId) {
        onSelect(nodeId)
      }
    },
    [valueToNodeId, onSelect],
  )

  return (
    <Autocomplete
      placeholder="Buscar recurso (nome, tipo)..."
      leftSection={<IconSearch size={16} />}
      data={options}
      onChange={handleChange}
      limit={10}
      comboboxProps={{ transitionProps: { transition: 'fade', duration: 100 } }}
    />
  )
}
